import { ComponentVariation } from '@/api/ComponentVariationApi';
import {
	CreatorState,
	CreatorStep,
	CreatorWindow,
	CreatorWing,
	SegmentDivisionType,
	SegmentJoint,
	SerializedCreator,
	WingDivisionType,
} from '@/types/creator';
import { Relation, RelationAction, RelationType, UUID } from '@/types/types';
import { AttributeValue } from '@/api/AttributeValueApi';
import { WritableDraft } from '@reduxjs/toolkit';
import { ConfigurationArticle, Project } from '@/api/ProjectApi';
import { ProjectTemplate } from '@/api/ProjectTemplateApi';
import { ContentContext } from '@/providers/ContentProvider';
import APP_CONFIG from '@/configs/appConfig';

export function getAvailableAttributes(
	componentVariations: ComponentVariation[]
) {
	const available: WritableDraft<CreatorState>['availableAttributes'] = {};

	for (const componentVariation of componentVariations) {
		const { componentId } = componentVariation;
		if (!available[componentId]) available[componentId] = [];

		for (const attributeValue of componentVariation.attributeValues) {
			const attribute = available[componentId].find(
				(attr) => attr.id === attributeValue.attribute.id
			);

			if (!attribute) {
				available[componentId].push({
					...attributeValue.attribute,
					attributeValues: [attributeValue],
					filteredValues: [{ ...attributeValue, disabled: false }],
				});
				continue;
			}

			if (
				attribute.attributeValues.some((attr) => attr.id === attributeValue.id)
			)
				continue;

			attribute.attributeValues.push(attributeValue);
			attribute.filteredValues.push({ ...attributeValue, disabled: false });
		}

		for (const attribute of available[componentId]) {
			attribute.attributeValues.sort((a, b) => a.listOrder - b.listOrder);
		}

		available[componentId].sort((a, b) => a.listOrder - b.listOrder);
	}

	return available;
}

export function mapCreatorStepName(
	step: CreatorStep,
	getContent: ContentContext['getContent']
) {
	const baseSlug = 'creatorStep.';

	switch (step) {
		case CreatorStep.ATTRIBUTE_VALUES:
			return getContent(`${baseSlug}systemTypeLabel`);
		case CreatorStep.DIMENSIONS:
			return getContent(`${baseSlug}dimensionsLabel`);
		case CreatorStep.COLORS:
			return getContent(`${baseSlug}colorsLabel`);
		case CreatorStep.SUMMARY:
			return getContent(`${baseSlug}summaryLabel`);
	}
}

export function setAttributesAvailability(state: CreatorState) {
	let componentId: UUID;
	for (componentId in state.availableAttributes) {
		const creatorAttributes = state.availableAttributes[componentId];

		const valuesSet = new Set<UUID>(
				Object.values(state.selectedValues)
					.filter(Boolean)
					.map((val) => val!.id)
			),
			variationsSet = new Set<UUID>(
				state.creatorProject.componentVariations.map((varia) => varia.id)
			);

		const componentVariations = filterComponentVariations(
			state.creatorComponents.find((creaComp) => creaComp.id === componentId)!
				.variations,
			valuesSet,
			variationsSet
		);

		for (let index = 0; index < creatorAttributes.length; index++) {
			const creatorAttribute = creatorAttributes[index];

			for (const creatorAttributeValue of creatorAttribute.filteredValues) {
				const selectedValuesAbove = creatorAttributes
					.slice(0, index)
					.map((attr) => state.selectedValues[attr.id])
					.filter(Boolean) as AttributeValue[];

				const isAvailable = componentVariations.some((componentVariation) => {
					const variationAttributeValues = componentVariation.attributeValues;

					const hasCurrentAttributeValue = variationAttributeValues.some(
						(attrValue) => attrValue.id === creatorAttributeValue.id
					);

					if (!hasCurrentAttributeValue) return false;

					return selectedValuesAbove.every((selectedValue) =>
						variationAttributeValues.some(
							(attrValue) => attrValue.id === selectedValue.id
						)
					);
				});

				creatorAttributeValue.disabled = !isAvailable;

				if (
					!isAvailable &&
					state.selectedValues[creatorAttribute.id]?.id ===
						creatorAttributeValue.id
				)
					state.selectedValues[creatorAttribute.id] = null;
			}
		}
	}
}

export function setComponentVariations(state: CreatorState) {
	const values = Object.values(state.selectedValues).filter(
			Boolean
		) as AttributeValue[],
		variations = state.creatorComponents.flatMap((comp) => comp.variations);

	state.creatorProject.componentVariations = variations.filter((variation) =>
		variation.attributeValues.every((av) => values.some((v) => v.id === av.id))
	);
}

export function setConfigurationArticles(state: CreatorState) {
	const previousByComponentVariationArticleId = new Map(
		state.creatorProject.configurationArticles.map((ca) => [
			ca.componentVariationArticleId,
			ca,
		])
	);

	state.creatorProject.configurationArticles =
		state.creatorProject.componentVariations.flatMap((variation) =>
			variation.componentVariationArticles.map((componentVariationArticle) => {
				const previous = previousByComponentVariationArticleId.get(
					componentVariationArticle.id
				);
				if (previous) return previous;

				return {
					componentVariationArticle,
					componentVariationArticleId: componentVariationArticle.id,
					code: componentVariationArticle.article.code,
					color: null,
					colorId: null,
					quantity: 1,
				};
			})
		);
}

function filterComponentVariations(
	componentVariations: ComponentVariation[],
	selectedValues: Set<UUID>,
	selectedVariations: Set<UUID>
) {
	return componentVariations.filter((variation) => {
		if (!variation.relations.length) return true;

		return variation.relations.some((relation) =>
			isRelationFullfiled(relation, selectedValues, selectedVariations)
		);
	});
}

function isRelationFullfiled(
	relation: Relation,
	selectedValues: Set<UUID>,
	selectedVariations: Set<UUID>
) {
	let match = false;
	switch (relation.type) {
		case RelationType.ATTRIBUTE_VALUE:
			match = selectedValues.has(relation.objectId);
			break;
		case RelationType.COMPONENT_VARIATION:
			match = selectedVariations.has(relation.objectId);
			break;
	}

	return match !== (relation.action === RelationAction.HIDE);
}

export function formatAttributeValueName(
	attributeValue: AttributeValue,
	getEntityText: ContentContext['getEntityText']
) {
	const displayName = getEntityText(attributeValue, 'displayName');
	if (!attributeValue.attribute.suffix)
		return displayName || attributeValue.value;

	return `${displayName || attributeValue.value} ${attributeValue.attribute.suffix}`.trim();
}

export function formatSegmentDivisionTypeName(
	divisionType: SegmentDivisionType
) {
	switch (divisionType) {
		case SegmentDivisionType.NONE:
			return 'Brak';
		case SegmentDivisionType.THIN:
			return 'Wąski';
		case SegmentDivisionType.WIDE:
			return 'Szeroki';
	}
}

export function formatWingDivisionTypeName(divisionType: WingDivisionType) {
	switch (divisionType) {
		case WingDivisionType.NONE:
			return 'Brak';
		case WingDivisionType.PRESENT:
			return 'Obecny';
	}
}

export function serializeCreator(state: CreatorState): SerializedCreator {
	const selectedValuesIds: SerializedCreator['selectedValuesIds'] = {};
	let attributeId: UUID;
	for (attributeId in state.selectedValues) {
		const selectedValue = state.selectedValues[attributeId];
		selectedValuesIds[attributeId] = selectedValue?.id || null;
	}

	return {
		configurationConfig: state.configurationConfig,
		segments: state.segments,
		projectDimensions: state.projectDimensions,
		segmentJoints: state.segmentJoints,
		selectedValuesIds,
	};
}

function setCreatorStateFromLoadedData(
	state: WritableDraft<CreatorState>,
	loaded: Project | ProjectTemplate
) {
	state.configurationConfig = loaded.serializedCreator.configurationConfig;

	const { segment } = state.configurationConfig,
		defaultMinWidth = APP_CONFIG.CONFIGURATION_DEFAULTS.SEGMENT.MIN_WIDTH,
		defaultMaxWidth = APP_CONFIG.CONFIGURATION_DEFAULTS.SEGMENT.MAX_WIDTH;

	segment.minWidth = defaultMinWidth;
	segment.maxWidth = defaultMaxWidth;

	if (
		!('recommendedMinWidthRules' in segment) ||
		!segment.recommendedMinWidthRules
	) {
		segment.recommendedMinWidthRules = [
			{ value: defaultMinWidth, withMosquitoNet: false },
			{ value: defaultMinWidth, withMosquitoNet: true },
		];
	}

	if (
		!('recommendedMinWidth' in segment) ||
		segment.recommendedMinWidth == null
	)
		segment.recommendedMinWidth =
			segment.recommendedMinWidthRules[
				state.configurationConfig.project.allowMosquitoNet ? 1 : 0
			].value;

	if (
		!('recommendedMaxWidth' in segment) ||
		segment.recommendedMaxWidth == null
	)
		segment.recommendedMaxWidth = defaultMaxWidth;

	state.segments = loaded.serializedCreator.segments;
	for (const segment of state.segments)
		segment.isBelowMinRecommendedWidth =
			segment.width < state.configurationConfig.segment.recommendedMinWidth;

	state.projectDimensions = loaded.serializedCreator.projectDimensions;
	state.segmentJoints = loaded.serializedCreator.segmentJoints;

	let attributeId: UUID;
	for (attributeId in state.selectedValues) {
		const selectedId = loaded.serializedCreator.selectedValuesIds[attributeId];

		if (!selectedId) continue;

		const attributeValue = Object.values(state.availableAttributes)
			.flat()
			.map((attr) => attr.attributeValues)
			.flat()
			.find((av) => av.id === selectedId);

		if (attributeValue) state.selectedValues[attributeId] = attributeValue;
	}
}

export function loadProject(
	state: WritableDraft<CreatorState>,
	project: Project
) {
	state.creatorProject.quantity = project.quantity;
	state.creatorProject.description = project.description;
	state.creatorProject.componentVariations = project.componentVariations;
	state.creatorProject.configurationArticles = project.configurationArticles;
	setCreatorStateFromLoadedData(state, project);
}

export function loadProjectTemplate(
	state: WritableDraft<CreatorState>,
	projectTemplate: ProjectTemplate
) {
	state.creatorProjectTemplate.name = projectTemplate.name;

	setCreatorStateFromLoadedData(state, projectTemplate);
}

export function evaluateFormula(formula: string, vars: Record<string, number>) {
	if (!formula) return 0;

	let parsedFormula = formula.replaceAll(' ', '');

	// longest first, so e.g. $ssw does not replace the prefix of $sswh
	const variables = Object.keys(vars).sort((a, b) => b.length - a.length);
	for (const variable of variables)
		parsedFormula = parsedFormula.replaceAll(
			variable,
			vars[variable].toString()
		);

	if (!/^[\d.+\-*/()]+$/.test(parsedFormula)) return 0;

	try {
		const result = eval(parsedFormula);
		if (!Number.isFinite(result)) return 0;

		return result as number;
	} catch {
		return 0;
	}
}

export function parseFormula(formula: string, vars: Record<string, number>) {
	return Math.floor(evaluateFormula(formula, vars));
}

export function hasMosquitoNetRunner(
	divisionType: WingDivisionType | undefined,
	neighbourWithMosquitoNet = false
) {
	return divisionType !== WingDivisionType.NONE || !neighbourWithMosquitoNet;
}

export function hasMosquitoNetExceedsMaxWidth(
	state: CreatorState,
	wing: CreatorWing
) {
	if (!state.configurationConfig.project.allowMosquitoNet) return false;
	if (!wing.withMosquitoNet) return false;

	let creatorWindow!: CreatorWindow;
	for (const segment of state.segments) {
		if (segment.window.wings.some((w) => w.id === wing.id)) {
			creatorWindow = segment.window;
			break;
		}
	}

	if (!creatorWindow) return false;

	const wingSequence = getWingSequences(
		creatorWindow.wings,
		creatorWindow.wingDivisions
	).find((sequence) => sequence.some((w) => w.id === wing.id));

	if (!wingSequence) return false;

	const wingsSectionTotalWidth = wingSequence.reduce((acc, curr) => {
		if (!curr.withMosquitoNet) return acc;
		return acc + curr.width;
	}, 0);

	return (
		wingsSectionTotalWidth > state.configurationConfig.wing.mosquitoNetMaxWidth
	);
}

export function setWingsMosquitoErrors(state: WritableDraft<CreatorState>) {
	for (const segment of state.segments) {
		for (const wing of segment.window.wings) {
			wing.isMosquitoError = hasMosquitoNetExceedsMaxWidth(state, wing);
		}
	}
}

export function clearInvalidWingDivisions(
	creatorWindow: WritableDraft<CreatorWindow>
) {
	const { wings, wingDivisions } = creatorWindow;

	for (let idx = 0; idx < wingDivisions.length; idx++) {
		if (wingDivisions[idx] !== WingDivisionType.PRESENT) continue;
		if (wings[idx]?.withMosquitoNet && wings[idx + 1]?.withMosquitoNet)
			continue;
		wingDivisions[idx] = WingDivisionType.NONE;
	}
}

export function setJointDivisionType(
	joint: WritableDraft<SegmentJoint>,
	divisionType: SegmentDivisionType
) {
	joint.divisionType = divisionType;
	if (divisionType !== SegmentDivisionType.THIN) joint.driverJunction = false;
}

export function getSegmentSequences<T>(
	segments: T[],
	segmentJoints: SegmentJoint[]
) {
	const sequences: T[][] = [[]];
	for (const joint of segmentJoints) {
		if (joint.divisionType === SegmentDivisionType.NONE) continue;
		sequences.push([]);
	}

	let currentSequencesIndex = 0;
	segments.forEach((segment, idx) => {
		sequences[currentSequencesIndex].push(segment);
		if (
			segmentJoints[idx] &&
			segmentJoints[idx].divisionType !== SegmentDivisionType.NONE
		)
			currentSequencesIndex++;
	});

	return sequences;
}

export function getWingSequences<T>(
	wings: T[],
	wingDivisions: WingDivisionType[]
) {
	const sequences: T[][] = [[]];
	for (const divisionType of wingDivisions) {
		if (divisionType === WingDivisionType.NONE) continue;
		sequences.push([]);
	}

	let currentSequencesIndex = 0;
	wings.forEach((wing, idx) => {
		sequences[currentSequencesIndex].push(wing);
		if (wingDivisions[idx] === WingDivisionType.PRESENT)
			currentSequencesIndex++;
	});

	return sequences;
}

export function setSegmentsArmorErrors(state: WritableDraft<CreatorState>) {
	if (!state.configurationConfig.project.withArmor) {
		for (const segment of state.segments) segment.isArmorError = false;
		return;
	}

	const sequences = getSegmentSequences(state.segments, state.segmentJoints);
	const maxWidth = state.configurationConfig.segment.recommendedMaxWidth;

	for (const sequence of sequences) {
		const sequenceTotalWidth = sequence.reduce(
			(acc, curr) => acc + curr.width,
			0
		);
		const isArmorError = sequenceTotalWidth > maxWidth;
		for (const segment of sequence) segment.isArmorError = isArmorError;
	}
}

export function getConfigurationArticleCode(
	configurationArticle: ConfigurationArticle
) {
	let code = configurationArticle.componentVariationArticle.article.code;
	if (configurationArticle.color) code += ` ${configurationArticle.color.code}`;
	return code;
}
